import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/Auth0Context.jsx';
import { apiUrl } from '../config/api.js';
import './RegistrationFlow.css';

/**
 * RegistrationFlow component handles the user registration process.
 *
 * Flow:
 * 1. Check if user has completed registration
 * 2. If not, show EULA acceptance screen
 * 3. Allow user to opt-in to emails
 * 4. Complete registration on backend
 * 5. Grant access (auto-admit) or show pending manual approval
 */
const RegistrationFlow = ({ onComplete }) => {
  const { isAuthenticated, user, getAccessTokenSilently } = useAuth();
  const [step, setStep] = useState('loading'); // loading, eula, pending-approval, completed, error
  const [eulaData, setEulaData] = useState(null);
  const [eulaAccepted, setEulaAccepted] = useState(false);
  const [emailOptIn, setEmailOptIn] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check registration status on mount
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    checkRegistrationStatus();
  }, [isAuthenticated]);

  const checkRegistrationStatus = async () => {
    try {
      const token = await getAccessTokenSilently();

      // Check registration status
      const statusResponse = await fetch(apiUrl('/api/auth/registration-status'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!statusResponse.ok) {
        throw new Error('Failed to check registration status');
      }

      const status = await statusResponse.json();

      // If completed and authorized, grant app access.
      if (status.registration_status === 'completed' && status.is_authorized) {
        setStep('completed');
        if (onComplete) {
          onComplete();
        }
        return;
      }

      // Registration completed but awaiting manual approval.
      if (status.registration_status === 'completed') {
        setStep('pending-approval');
        return;
      }

      // Load EULA
      const eulaResponse = await fetch(apiUrl('/api/auth/eula'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!eulaResponse.ok) {
        throw new Error('Failed to load EULA');
      }

      const eula = await eulaResponse.json();
      setEulaData(eula);
      setStep('eula');
    } catch (err) {
      console.error('Error checking registration status:', err);
      setError(err.message || 'Failed to load registration information');
      setStep('error');
    }
  };

  const handleCompleteRegistration = async () => {
    if (!eulaAccepted) {
      setError('You must accept the Terms of Service to continue');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();

      const response = await fetch(apiUrl('/api/auth/complete-registration'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          eula_accepted: eulaAccepted,
          eula_version: eulaData.version,
          email_opt_in: emailOptIn,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        const errorData = data || {};
        throw new Error(errorData.detail || 'Failed to complete registration');
      }

      if (data.status === 'pending_approval') {
        setStep('pending-approval');
        return;
      }

      // Registration complete and approved.
      setStep('completed');
      if (onComplete) {
        onComplete();
      }
    } catch (err) {
      console.error('Error completing registration:', err);
      setError(err.message || 'Failed to complete registration');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (step === 'loading') {
    return (
      <div className="registration-flow">
        <div className="registration-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading registration information...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (step === 'error') {
    return (
      <div className="registration-flow">
        <div className="registration-container">
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <h2>Registration Error</h2>
            <p>{error}</p>
            <button onClick={checkRegistrationStatus} className="retry-button">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Completed state
  if (step === 'completed') {
    return (
      <div className="registration-flow">
        <div className="registration-container">
          <div className="success-state">
            <div className="success-icon">✅</div>
            <h2>Registration Complete!</h2>
            <p>Welcome to Fable Table! You now have access to Tavern, Character Maker, and Tutorial.</p>
            <div className="redirect-message">Redirecting you to the app...</div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'pending-approval') {
    return (
      <div className="registration-flow processing-flow">
        <div className="processing-background">
          <div className="processing-overlay">
            <div className="processing-content">
              <h2>Registration Received</h2>
              <p className="processing-message">Your account is awaiting manual approval</p>
              <div className="processing-details">
                <p>
                  We are onboarding users in controlled waves after the initial auto-admit phase.
                </p>
                <p>
                  You&apos;ll receive access once your registration is approved for <strong>{user?.email || 'your email address'}</strong>.
                </p>
                <p>
                  After approval, you can enter Tavern, Character Maker, and Tutorial immediately.
                </p>
              </div>
              <div className="billing-actions">
                <button onClick={checkRegistrationStatus} className="submit-button">
                  Check Approval Status
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // EULA acceptance step
  return (
    <div className="registration-flow">
      <div className="registration-container">
        <div className="registration-header">
          <div className="alpha-badge">PRIVATE ALPHA</div>
          <h1>Welcome to Fable Table!</h1>
          <p className="welcome-message">
            Before you begin your adventure, please review and accept our Terms of Service.
          </p>
        </div>

        <div className="eula-container">
          <div className="eula-header">
            <h2>Fable Table Terms of Service</h2>
            <span className="eula-version">Version {eulaData?.version}</span>
          </div>
          <div className="eula-content">
            <pre>{eulaData?.content}</pre>
          </div>
        </div>

        <div className="registration-form">
          <div className="form-section">
            <label className="checkbox-label eula-acceptance">
              <input
                type="checkbox"
                checked={eulaAccepted}
                onChange={(e) => setEulaAccepted(e.target.checked)}
              />
              <span>
                I have read and agree to the Fable Table Terms of Service
              </span>
            </label>
          </div>

          <div className="form-section">
            <label className="checkbox-label email-optin">
              <input
                type="checkbox"
                checked={emailOptIn}
                onChange={(e) => setEmailOptIn(e.target.checked)}
              />
              <span>
                I would like to receive updates and important announcements
              </span>
            </label>
            <p className="help-text">
              We'll send you important updates and announcements.
              Your email will never be shared with third parties.
            </p>
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <div className="form-actions">
            <button
              onClick={handleCompleteRegistration}
              disabled={!eulaAccepted || isSubmitting}
              className="submit-button"
            >
              {isSubmitting ? 'Completing Registration...' : 'Accept & Continue'}
            </button>
            <p className="legal-notice">
              By clicking this button, you acknowledge that you understand and accept the agreement above and our{' '}
              <Link to="/privacy" className="underline hover:text-amber-400 transition-colors">
                Privacy Policy
              </Link>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationFlow;
